import React, { createContext, useContext, useRef, useCallback } from "react";

interface PendingNotesContextValue {
  register: (id: string, flush: () => Promise<void>) => void;
  unregister: (id: string) => void;
  flushAll: () => Promise<void>;
}

const PendingNotesContext = createContext<PendingNotesContextValue>({
  register: () => {},
  unregister: () => {},
  flushAll: async () => {},
});

export const PendingNotesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const callbacks = useRef<Map<string, () => Promise<void>>>(new Map());

  const register = useCallback((id: string, flush: () => Promise<void>) => {
    callbacks.current.set(id, flush);
  }, []);

  const unregister = useCallback((id: string) => {
    callbacks.current.delete(id);
  }, []);

  const flushAll = useCallback(async () => {
    const promises = Array.from(callbacks.current.values()).map((fn) => fn());
    await Promise.all(promises);
    callbacks.current.clear();
  }, []);

  return (
    <PendingNotesContext.Provider value={{ register, unregister, flushAll }}>
      {children}
    </PendingNotesContext.Provider>
  );
};

export const usePendingNotes = () => useContext(PendingNotesContext);
